import type { ReactNode } from 'react';

/** NotificationBanner 唯一实际渲染的背景主题。 */
export const NOTIFICATION_BANNER_VARIANT = 'gradient-pink' as const;

/**
 * Banner 唯一支持的背景样式。
 */
export type BannerVariant = typeof NOTIFICATION_BANNER_VARIANT;

/** Banner 前景视觉：标准公告或 Workspace 推广。 */
export type NotificationBannerAppearance = 'standard' | 'promotion';

/**
 * CTA 鏈接類型
 * - externalLink: 外部鏈接（新窗口打開）
 * - internalLink: 內部鏈接（SPA 導航）
 */
export type CtaLinkType = 'externalLink' | 'internalLink';

/**
 * Single item in the whole-banner carousel treatment.
 */
export interface NotificationBannerCarouselItem {
    /** Banner id used for render keys, analytics, and read state */
    id: string;

    /**
     * 历史背景样式字段。
     *
     * @deprecated 当前固定渲染 `gradient-pink`。使用 string 是为了兼容历史调用方传入的旧值。
     */
    variant?: string;

    /** Banner content for this item */
    message: ReactNode;

    /** CTA button text for this item */
    ctaText?: string;

    /** CTA link for this item */
    ctaLink?: string;

    /** CTA link type for this item */
    ctaLinkType?: CtaLinkType;

    /** Whether to show CTA for this item */
    showCta?: boolean;

    /** Whether to show close button for this item */
    showCloseButton?: boolean;

    /** Absolute UTC deadline resolved by the backend for the current user */
    countdownEndTime?: string | null;
}

/**
 * Props for the NotificationBanner component
 */
export interface NotificationBannerProps {
    /** Banner 唯一標識符，用於追蹤已讀狀態 */
    id: string;

    /** 前景视觉样式 (default: 'standard') */
    appearance?: NotificationBannerAppearance;

    /**
     * 历史背景样式字段。
     *
     * @deprecated 当前固定渲染 `gradient-pink`，传入其它值也不会改变背景。使用 string
     * 是为了兼容历史调用方传入的旧值。
     */
    variant?: string;

    /** 通知內容（支持文字或自定義 ReactNode，可包含 Emoji） */
    message: ReactNode;

    /** 整条 Banner 轮播项；不传时保持普通单条 Banner 布局 */
    carouselItems?: NotificationBannerCarouselItem[];

    /** 轮播切换间隔，单位 ms (default: 3000) */
    carouselIntervalMs?: number;

    /** CTA 按鈕文字 (default: 'Read more') */
    ctaText?: string;

    /** CTA 鏈接（path 或 URL），與 onCtaClick 成對使用 */
    ctaLink?: string;

    /** CTA 鏈接類型 (default: 'externalLink') */
    ctaLinkType?: CtaLinkType;

    /** 是否顯示 CTA 按鈕 (default: false) */
    showCta?: boolean;

    /** CTA 點擊回調，點擊時傳入完整的 banner props，由調用方處理跳轉或其他操作 */
    onCtaClick?: (banner: NotificationBannerProps) => void;

    /** 是否顯示關閉按鈕 (default: true) */
    showCloseButton?: boolean;

    /** Absolute UTC deadline resolved by the backend for the current user */
    countdownEndTime?: string | null;

    /** 點擊關閉按鈕時的回調（用於標記已讀等），傳入當前激活的 Banner */
    onClose?: (banner?: NotificationBannerCarouselItem) => void;

    /** 自定義樣式類名 */
    className?: string;
}
